/**
 * react-router v7 route table for the CRO scorecard SPA.
 *
 * Routes:
 *   `/`                       → CroScorecardPage (4-section 11-tile grid)
 *   `/main`                   → MainScorecardPage (3-tab ~45-tile sales dashboard — Plan 05-12)
 *   `/forecast`               → ForecastPage (Combined Revenue Forecast exec tab — Phase 05.1)
 *   `/admin/cro-targets`      → AdminTargetsPage (Plan 04-09 Task 1)
 *   `/admin/finance-upload`   → FinanceUploadPage (Plan 05-04 Task 4)
 *   `/logout`                 → LogoutPage (Easy Auth sign-out)
 *   `*`                       → redirect to /
 *
 * Flat routes only (no nested layouts) — same pattern as packages/spa.
 *
 * Auth gating: Easy Auth platform layer (App Service) enforces authentication
 * before the SPA renders. 401 from the API bounces to /.auth/login/aad
 * (handled in apiFetch). Group-membership defense-in-depth happens at the
 * per-page level (useCroMe().groups for /admin/finance-upload).
 *
 * /main + /api/main/* bypass Easy Auth (Terraform excluded_paths per Plan 05-12 Task 3).
 * SF OAuth (Phase 1 AUTH-01..06) is the authoritative gate for /main.
 */
import { Navigate, createBrowserRouter } from 'react-router';

import { FinanceUploadPage } from './admin/FinanceUploadPage.js';
import { AdminTargetsPage } from './AdminTargetsPage.js';
import { CroScorecardPage } from './CroScorecardPage.js';
import { ForecastPage } from './ForecastPage.js';
import { LogoutPage } from './LogoutPage.js';
import { MainScorecardPage } from './MainScorecardPage.js';
import { Plan2027Page } from './Plan2027Page.js';

// Root route is host-aware:
//   - cro.salesdashboard.adeptia.org → CRO scorecard (executive Paul Report)
//   - salesdashboard.adeptia.org (apex) → redirect to /main (Sales dashboard)
// Same bundle ships to both hosts; the active landing changes by domain.
// Admin pages (/admin/*) work natively on both hosts because Easy Auth is
// now configured on the apex Web App too (AllowAnonymous + CRO group
// restriction at sign-in time) — see 2026-06-17 infra change.
function RootLanding(): React.JSX.Element {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (!host.startsWith('cro.') && !host.startsWith('localhost') && host !== '127.0.0.1') {
      return <Navigate to="/main" replace />;
    }
  }
  return <CroScorecardPage />;
}

export const router = createBrowserRouter([
  { path: '/', element: <RootLanding /> },
  { path: '/main', element: <MainScorecardPage /> },
  // /forecast — Combined Revenue Forecast exec tab (Phase 05.1). Host-aware
  // reachable on apex + cro like /main; SF-OAuth read gate enforced by the API.
  { path: '/forecast', element: <ForecastPage /> },
  // /2027-plan — live V1/V2/V3 FY2027 pipeline + bookings plan (SF-OAuth read
  // gate enforced by the API; reachable on apex + cro like /main + /forecast).
  { path: '/2027-plan', element: <Plan2027Page /> },
  // /metric-trends and its first name /funnel-trends — the trends are no longer a
  // standalone tab; they are section 5 of the three-year model on /2027-plan
  // (quick-261013-hqb, John 2026-10-13). Old links land on that section.
  { path: '/metric-trends', element: <Navigate to="/2027-plan#metric-trends" replace /> },
  { path: '/funnel-trends', element: <Navigate to="/2027-plan#metric-trends" replace /> },
  { path: '/admin/cro-targets', element: <AdminTargetsPage /> },
  { path: '/admin/finance-upload', element: <FinanceUploadPage /> },
  { path: '/logout', element: <LogoutPage /> },
  { path: '*', element: <Navigate to="/" replace /> },
]);
